import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';

const SignUpPeople = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [signupList, setSignupList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  // Table from database table signed_up_data (email_id, name, phone_no, chatbot_name)
  const [signedUpDataList, setSignedUpDataList] = useState([]);
  const [signedUpDataLoading, setSignedUpDataLoading] = useState(false);
  const [signedUpDataSearch, setSignedUpDataSearch] = useState('');
  const [creatingRowEmail, setCreatingRowEmail] = useState(null);
  // Emails for which Create User was already clicked (success) — button stays disabled
  const [createdUserEmails, setCreatedUserEmails] = useState(() => new Set());

  const isAdmin = () => {
    const chatbotUser = localStorage.getItem('chatbotUser');
    let userEmail = '';
    if (chatbotUser) {
      try {
        const user = JSON.parse(chatbotUser);
        userEmail = user.email || '';
      } catch (e) {
        console.error('Error parsing chatbotUser:', e);
      }
    }
    if (!userEmail) userEmail = localStorage.getItem('userEmail') || '';
    return userEmail.toLowerCase() === 'info@urcommunity.com.au';
  };

  const fetchSignUpPeople = async () => {
    setLoading(true);
    setError(null);
    try {
      const chatbotUser = localStorage.getItem('chatbotUser');
      let userEmail = '';
      if (chatbotUser) {
        try {
          const user = JSON.parse(chatbotUser);
          userEmail = user.email || '';
        } catch (e) {
          console.error('Error parsing chatbotUser:', e);
        }
      }
      if (!userEmail) userEmail = localStorage.getItem('userEmail') || '';

      const response = await fetch(
        `${API_BASE_URL}/api/admin/signup-people?email=${encodeURIComponent(userEmail)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-admin-email': userEmail
          }
        }
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.success) {
        setSignupList(data.data || []);
      } else {
        throw new Error(data.error || 'Failed to fetch sign up people');
      }
    } catch (err) {
      if (err.message.includes('Failed to fetch')) {
        setError('Backend server not running. Please start the server on port 3001.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const getAdminEmail = () => {
    const chatbotUser = localStorage.getItem('chatbotUser');
    let email = '';
    if (chatbotUser) {
      try {
        const user = JSON.parse(chatbotUser);
        email = (user.email || '').toString().trim();
      } catch (e) {
        console.error('Error parsing chatbotUser:', e);
      }
    }
    if (!email) email = (localStorage.getItem('userEmail') || '').toString().trim();
    return email;
  };

  const fetchSignedUpData = async () => {
    setSignedUpDataLoading(true);
    setError(null);
    try {
      const userEmail = getAdminEmail();
      if (!userEmail) {
        setError('Admin email not found. Please log in again.');
        return;
      }

      const url = `${API_BASE_URL}/api/admin/signed-up-data?email=${encodeURIComponent(userEmail)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-email': userEmail
        }
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }
      if (data.success === true && Array.isArray(data.data)) {
        setSignedUpDataList(data.data);
      } else {
        setSignedUpDataList([]);
      }
    } catch (err) {
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        setError('Backend server not running or unreachable. Please start the server (e.g. port 3001) and ensure API_BASE_URL is correct.');
      } else {
        setError(err.message);
      }
    } finally {
      setSignedUpDataLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin()) {
      setError('Access denied. Admin privileges required.');
      return;
    }
    fetchSignUpPeople();
    fetchSignedUpData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredList = signupList.filter((row) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (row.email || '').toLowerCase().includes(searchLower) ||
      (row.full_name || '').toLowerCase().includes(searchLower) ||
      (row.chatbot_name || '').toLowerCase().includes(searchLower) ||
      (row.phone || '').toLowerCase().includes(searchLower) ||
      (row.status || '').toLowerCase().includes(searchLower)
    );
  });

  const filteredSignedUpData = signedUpDataList.filter((row) => {
    const searchLower = signedUpDataSearch.toLowerCase();
    return (
      (row.email_id || '').toLowerCase().includes(searchLower) ||
      (row.name || '').toLowerCase().includes(searchLower) ||
      (row.phone_no || '').toLowerCase().includes(searchLower) ||
      (row.chatbot_name || '').toLowerCase().includes(searchLower) ||
      (row.appointment_at ? formatDate(row.appointment_at).toLowerCase().includes(searchLower) : false) ||
      (row.appointment_no != null && String(row.appointment_no).includes(signedUpDataSearch.trim()))
    );
  });

  const handleCreateUserAndSendPayment = async (row) => {
    const emailId = (row.email_id || '').trim();
    const name = (row.name || '').trim();
    if (!emailId || !name) {
      setError('Row missing email or name.');
      return;
    }
    setCreatingRowEmail(emailId);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/signup/create-user-and-send-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email_id: emailId,
          name,
          phone_no: (row.phone_no || '').trim(),
          chatbot_name: (row.chatbot_name || '').trim()
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create user or send payment link');
      }
      setCreatedUserEmails((prev) => new Set(prev).add(emailId));
      alert(data.message || 'User created and payment link email sent.');
    } catch (err) {
      console.error('Create user error:', err);
      setError(err.message || 'Failed to create user.');
      alert('Error: ' + (err.message || 'Unknown error'));
    } finally {
      setCreatingRowEmail(null);
    }
  };

  if (!isAdmin()) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2 style={{ color: '#dc2626' }}>Access Denied</h2>
        <p>Admin privileges required to access this page.</p>
      </div>
    );
  }

  return (
    <div style={{ background: '#f9fafb', minHeight: '100vh', padding: '24px' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', margin: '0 0 8px 0' }}>
            Sign Up People
          </h1>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
            View details of people who signed up via the Sign Up form (basic details sent for creating agent)
          </p>
        </div>

        {error && (
          <div style={{
            background: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px',
            color: '#dc2626',
            marginBottom: '16px'
          }}>
            {error}
            <button
              onClick={() => setError(null)}
              style={{ background: 'none', border: 'none', color: '#dc2626', float: 'right', cursor: 'pointer', fontSize: '16px' }}
            >
              ×
            </button>
          </div>
        )}

        {/* Single box: search bar at top + table from signed_up_data */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              Search
            </label>
            <button
              type="button"
              onClick={() => fetchSignedUpData()}
              disabled={signedUpDataLoading}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#7c3aed',
                background: '#ede9fe',
                border: '1px solid #c4b5fd',
                borderRadius: '6px',
                cursor: signedUpDataLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {signedUpDataLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
          <input
            type="text"
            placeholder="Search by email_id, name, phone_no, chatbot_name..."
            value={signedUpDataSearch}
            onChange={(e) => setSignedUpDataSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box',
              marginBottom: '20px'
            }}
          />
          <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 380px)', overflowY: 'auto' }}>
            {signedUpDataLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', color: '#7c3aed' }}></i>
              </div>
            ) : filteredSignedUpData.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                No records in signed_up_data table.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Email Id</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Name</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Phone No</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Chatbot Name</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Appointment</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Appointment No</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSignedUpData.map((row, idx) => (
                    <tr key={row.id != null ? row.id : `signed-up-${idx}`} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px', color: '#111827' }}>{row.email_id || '—'}</td>
                      <td style={{ padding: '12px', color: '#111827' }}>{row.name || '—'}</td>
                      <td style={{ padding: '12px', color: '#111827' }}>{row.phone_no || '—'}</td>
                      <td style={{ padding: '12px', color: '#111827' }}>{row.chatbot_name || '—'}</td>
                      <td style={{ padding: '12px', color: '#111827' }}>{row.appointment_at ? formatDate(row.appointment_at) : '—'}</td>
                      <td style={{ padding: '12px', color: '#111827' }}>{row.appointment_no != null ? row.appointment_no : '—'}</td>
                      <td style={{ padding: '12px' }}>
                        <button
                          type="button"
                          onClick={() => handleCreateUserAndSendPayment(row)}
                          disabled={creatingRowEmail !== null || createdUserEmails.has((row.email_id || '').trim())}
                          style={{
                            padding: '6px 12px',
                            fontSize: '13px',
                            fontWeight: '500',
                            color: createdUserEmails.has((row.email_id || '').trim()) ? '#9ca3af' : '#7c3aed',
                            background: creatingRowEmail === (row.email_id || '').trim() ? '#e5e7eb' : createdUserEmails.has((row.email_id || '').trim()) ? '#f3f4f6' : '#ede9fe',
                            border: '1px solid #c4b5fd',
                            borderRadius: '6px',
                            cursor: creatingRowEmail !== null || createdUserEmails.has((row.email_id || '').trim()) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {creatingRowEmail === (row.email_id || '').trim() ? 'Creating...' : createdUserEmails.has((row.email_id || '').trim()) ? 'Sent' : 'Create User'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* COMMENTED OUT: extra search box and Sign ups table (signup_agent_requests) - existing code preserved
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <label>Search</label>
          <input type="text" placeholder="Search by email, name, chatbot name, phone..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        {loading && ( <div style={{ textAlign: 'center', padding: '40px' }}><i className="fas fa-spinner fa-spin"></i></div> )}
        {!loading && (
          <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <h2>Sign ups ({filteredList.length})</h2>
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
              {filteredList.length === 0 ? ( <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>No sign up records found.</div> ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                      <th>Email</th><th>Full Name</th><th>Phone</th><th>Chatbot Name</th><th>Chatbot Description</th><th>Generated Password</th><th>Status</th><th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredList.map((row) => (
                      <tr key={row.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td>{row.email || '—'}</td><td>{row.full_name || '—'}</td><td>{row.phone || '—'}</td><td>{row.chatbot_name || '—'}</td>
                        <td>{row.chatbot_description || '—'}</td><td>{row.generated_password || '—'}</td>
                        <td><span>{row.status || 'pending'}</span></td><td>{formatDate(row.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
        */}
      </div>
    </div>
  );
};

export default SignUpPeople;
